import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { API_BASE_URL } from "../config";
import { LOCATION_TYPES, sortByLocationType } from "../constants";
import { downloadCsv, downloadCsvTemplate, csvFilename } from "../csv";
import { combineLatLng, parseLatLng, normalizeLatLng, w3wUrl, mapsUrl, gridRefUrl, externalUrl,
         openAdornment, convertAdornment } from "../geo";
import { useLocationConvert } from "../useLocationConvert";
import { useMe } from "../MeContext";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";

const LOCATIONS_API = `${API_BASE_URL}/locations`;

const BLANK = {
  type: "", name: "", what3words: "", grid_ref: "", postcode: "",
  full_address: "", google_maps_url: "", lat_long: "",
};

// Canonical CSV column names (recognised by the backend).
const CSV_HEADERS = [
  "Type", "Name", "What3Words", "Grid reference", "Postcode", "Full address", "Google Maps", "Lat/long",
];

// One location as a row in CSV_HEADERS order — the same columns the upload
// recognises, so a downloaded file can be re-uploaded as-is (e.g. to carry this
// year's locations over to next year's event).
const csvRow = (l) => [
  l.type, l.name, l.what3words, l.grid_ref, l.postcode, l.full_address,
  l.google_maps_url, combineLatLng(l.latitude, l.longitude),
];

/**
 * The pre-defined locations of one event (start, aid stations, finish …), which an
 * incident can be placed at. Saved instantly — this dialog has no Save button of
 * its own; each location is added or edited in the dialog it opens.
 *
 * Props:
 *  - event:   the event record ({id, label_long, …}); null when closed
 *  - open:    boolean
 *  - onClose: () => void
 */
export default function EventLocationsDialog({ event, open, onClose }) {
  const { can } = useMe();
  const fullScreen = useFullScreenDialog();
  const eventId = event?.id;

  const [locations, setLocations] = useState([]);
  const [editing, setEditing] = useState(null);   // null | "new" | id
  const [form, setForm] = useState(BLANK);
  const [initial, setInitial] = useState(BLANK);  // form snapshot at open, to detect unsaved changes
  const [confirmClose, setConfirmClose] = useState(false); // discard-unsaved-changes prompt
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // {created, skipped} | {error}
  // Save posts a new location, so a double-tap would add two (see useWriteLock).
  const { busy: saving, run: runWrite } = useWriteLock();

  // The four location fields are inter-convertible; this shared hook fills the
  // other three (or all four, from a Grid Reference Finder link) preserving precision.
  const {
    busy, grUrl, setGrUrl, fromW3w, fromGridRef, fromLatLng, fromPostcode, fromGrUrl,
  } = useLocationConvert({
    setForm,
    keys: { w3w: "what3words", gr: "grid_ref", pc: "postcode", ll: "lat_long" },
  });

  const refresh = () =>
    axios.get(LOCATIONS_API + "/", { params: { event_id: eventId } })
      .then(res => setLocations(res.data.sort(sortByLocationType)));

  useEffect(() => {
    if (!open || !eventId) return;
    setEditing(null);
    setUploadResult(null);
    setConfirmDeleteAll(false);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, eventId]);

  const perms = {
    add: can("EventLocations.add", { eventId }), edit: can("EventLocations.edit", { eventId }),
    del: can("EventLocations.delete", { eventId }), deleteAll: can("EventLocations.deleteAll", { eventId }),
    upload: can("EventLocations.uploadCSV", { eventId }),
  };

  const openNew = () => { setForm(BLANK); setInitial(BLANK); setGrUrl(""); setEditing("new"); };
  const openEdit = (l) => {
    const loaded = {
      type: l.type ?? "", name: l.name ?? "", what3words: l.what3words ?? "",
      grid_ref: l.grid_ref ?? "", postcode: l.postcode ?? "",
      full_address: l.full_address ?? "", google_maps_url: l.google_maps_url ?? "",
      lat_long: combineLatLng(l.latitude, l.longitude),
    };
    setForm(loaded);
    setInitial(loaded);
    setGrUrl("");
    setEditing(l.id);
  };
  const closeEdit = () => { setEditing(null); setConfirmClose(false); setGrUrl(""); };
  // Closing (backdrop, Escape or Cancel) warns first if there are unsaved changes.
  const isDirty = () => JSON.stringify(form) !== JSON.stringify(initial);
  const requestCloseEdit = () => (isDirty() ? setConfirmClose(true) : closeEdit());
  const field = (key) => ({
    value: form[key],
    onChange: e => setForm({ ...form, [key]: e.target.value }),
  });
  const payload = () => {
    // Accept DMS/DDM in the lat/long field; store decimal degrees.
    const [lat, lng] = parseLatLng(normalizeLatLng(form.lat_long) || form.lat_long);
    return {
      type: form.type || null,
      name: form.name || null,
      what3words: form.what3words || null,
      grid_ref: form.grid_ref || null,
      postcode: form.postcode || null,
      full_address: form.full_address || null,
      google_maps_url: form.google_maps_url || null,
      latitude: lat,
      longitude: lng,
    };
  };
  const save = () => runWrite(() => {
    const after = () => { refresh(); closeEdit(); };
    return editing === "new"
      ? axios.post(LOCATIONS_API + "/", { event_id: eventId, ...payload() }).then(after)
      : axios.put(`${LOCATIONS_API}/${editing}`, payload()).then(after);
  });
  const remove = () =>
    axios.delete(`${LOCATIONS_API}/${editing}`).then(() => { refresh(); closeEdit(); });

  const uploadCsv = (file) => {
    if (!file) return;
    setUploadResult(null);
    const data = new FormData();
    data.append("event_id", eventId);
    data.append("file", file);
    axios.post(`${LOCATIONS_API}/upload`, data)
      .then(res => { setUploadResult(res.data); refresh(); })
      .catch(err => setUploadResult({ error: err.response?.data?.detail || "Upload failed." }));
  };

  const deleteAll = () =>
    axios.delete(LOCATIONS_API + "/", { params: { event_id: eventId } })
      .then(() => { setConfirmDeleteAll(false); setUploadResult(null); refresh(); });

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>Locations ({locations.length}) — {event?.label_long || event?.name}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {perms.add && (
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openNew}>
                  Add location
                </Button>
              )}
              {perms.upload && (
                <Button size="small" variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                  Upload CSV
                  <input type="file" accept=".csv,text/csv" hidden
                         onChange={e => { uploadCsv(e.target.files[0]); e.target.value = ""; }} />
                </Button>
              )}
              {/* Same columns as the upload, so this file can be re-uploaded
                  to another event (e.g. next year's running of this one). */}
              <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                      disabled={locations.length === 0}
                      onClick={() => downloadCsv(CSV_HEADERS, locations.map(csvRow),
                                                 csvFilename(event, "locations"))}>
                Download CSV
              </Button>
              {perms.upload && (
                <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />}
                        onClick={() => downloadCsvTemplate(CSV_HEADERS, "locations-template.csv")}>
                  CSV template
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              {perms.deleteAll && (
                <Button size="small" color="error" startIcon={<DeleteSweepIcon />}
                        disabled={locations.length === 0}
                        onClick={() => setConfirmDeleteAll(true)}>
                  Delete all
                </Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              CSV header row with any of: Type, Name, What3Words, Grid reference, Postcode, Lat/long.
            </Typography>
            {uploadResult && (uploadResult.error ? (
              <Alert severity="error" onClose={() => setUploadResult(null)}>{uploadResult.error}</Alert>
            ) : (
              <Alert severity="success" onClose={() => setUploadResult(null)}>
                Imported {uploadResult.created} location{uploadResult.created === 1 ? "" : "s"}
                {uploadResult.skipped ? `, skipped ${uploadResult.skipped} (empty)` : ""}.
              </Alert>
            ))}
            {locations.length > 0 ? (
              <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                      <TableCell>Type</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Grid ref</TableCell>
                      <TableCell>Postcode</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locations.map(l => (
                      <TableRow hover key={l.id} onClick={() => openEdit(l)} sx={{ cursor: "pointer" }}>
                        <TableCell>{l.type || "—"}</TableCell>
                        <TableCell>{l.name || "—"}</TableCell>
                        <TableCell>{l.grid_ref || "—"}</TableCell>
                        <TableCell>{l.postcode || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No locations yet. Add the places incidents happen at (start, aid stations,
                finish …) and they become pickable on an incident.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Location add / edit dialog */}
      <Dialog open={editing !== null} onClose={requestCloseEdit} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>{editing === "new" ? "Add location" : (form.name || "Edit location")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField label="Type" select sx={{ flex: 1, minWidth: 200 }} {...field("type")}>
                <MenuItem value=""><em>—</em></MenuItem>
                {LOCATION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField label="Name" sx={{ flex: 1, minWidth: 200 }} {...field("name")} />
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
              <TextField label="Grid Reference Finder link" sx={{ flex: 1 }}
                         placeholder="https://gridreferencefinder.com/os.php?gr=SD5190993629|…"
                         helperText="Paste a link, then Populate to fill all four location fields below"
                         value={grUrl}
                         onChange={e => setGrUrl(e.target.value)}
                         onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); fromGrUrl(); } }} />
              <Button variant="outlined" onClick={fromGrUrl}
                      disabled={!!busy || !grUrl.trim()} sx={{ mt: 0.5, flexShrink: 0 }}
                      startIcon={busy === "link" ? <CircularProgress size={16} /> : undefined}>
                Populate
              </Button>
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField label="what3words" sx={{ flex: 1, minWidth: 200 }}
                         placeholder="casino.wrong.chop" {...field("what3words")}
                         slotProps={convertAdornment(
                           { onClick: () => fromW3w(form), enabled: !!(form.what3words || "").trim(),
                             busy: busy === "w3w", tip: "Fill grid ref, lat/long & postcode from this what3words address" },
                           { url: w3wUrl(form.what3words), enabled: !!(form.what3words || "").trim(),
                             tip: "Open in what3words" })} />
              <TextField label="Grid reference" sx={{ flex: 1, minWidth: 160 }}
                         placeholder="NY268138" {...field("grid_ref")}
                         slotProps={convertAdornment(
                           { onClick: () => fromGridRef(form), enabled: !!(form.grid_ref || "").trim(),
                             busy: busy === "gr", tip: "Fill what3words, lat/long & postcode from this grid reference" },
                           { url: gridRefUrl(form.grid_ref), enabled: !!(form.grid_ref || "").trim(),
                             tip: "Open in Grid Reference Finder (OS mapping)" })} />
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField label="Postcode" sx={{ flex: 1, minWidth: 140 }}
                         placeholder="LA9 6JF" {...field("postcode")}
                         slotProps={convertAdornment(
                           { onClick: () => fromPostcode(form), enabled: !!(form.postcode || "").trim(),
                             busy: busy === "pc", tip: "Fill what3words, lat/long & grid reference from this postcode" },
                           { url: mapsUrl(form.postcode), enabled: !!(form.postcode || "").trim(),
                             tip: "Open in Google Maps" })} />
              <TextField label="Lat / long" sx={{ flex: 1, minWidth: 200 }}
                         placeholder="54.4512, -3.0125"
                         helperText="Decimal or DMS, e.g. 54°19′46″N, 002°43′55″W — converted on populate/save"
                         {...field("lat_long")}
                         slotProps={convertAdornment(
                           { onClick: () => fromLatLng(form), enabled: parseLatLng(form.lat_long)[0] != null,
                             busy: busy === "ll", tip: "Fill what3words, grid reference & postcode from this lat/long" },
                           { url: mapsUrl(form.lat_long), enabled: parseLatLng(form.lat_long)[0] != null,
                             tip: "Open in Google Maps" })} />
            </Box>
            <TextField label="Full address" fullWidth multiline minRows={2} {...field("full_address")} />
            <TextField label="Google Maps" fullWidth
                       placeholder="https://maps.app.goo.gl/…"
                       {...field("google_maps_url")}
                       slotProps={openAdornment(externalUrl(form.google_maps_url),
                                                !!(form.google_maps_url || "").trim(), "Open Google Maps link")} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1, borderTop: "1px solid", borderColor: "divider" }}>
              {editing !== "new" && perms.del && (
                <Button color="error" startIcon={<DeleteIcon />} onClick={remove}>Delete</Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button onClick={requestCloseEdit}>Cancel</Button>
              {(editing === "new" ? perms.add : perms.edit) && (
                <Button variant="contained" onClick={save} disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : undefined}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Location unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this location. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={closeEdit}>Discard changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete-all confirmation */}
      <Dialog open={confirmDeleteAll} onClose={() => setConfirmDeleteAll(false)}>
        <DialogTitle>Delete all locations?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete all {locations.length} location
            {locations.length === 1 ? "" : "s"} for {event?.name}. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteAll(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteSweepIcon />} onClick={deleteAll}>
            Delete all
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
